import '../styles/reset.scss';
import '../styles/globals.scss';
import '../styles/utility.scss';
import '../styles/swiper.scss';
import '../styles/ais.scss';

import Head from 'next/head';
import { DefaultSeo } from 'next-seo';
import { Inter } from 'next/font/google';

import { StoreProvider } from '@/context/store';
import { CartProvider } from '@/context/cart';
import { SiteSettingsContext } from '@/context/siteSettings';
import Layout from '@/layouts/Layout';
import { generateHSLShades } from '@/lib/colors';

import { UserProvider } from '@/context/user';
import { SpecialsProvider } from '@/context/specials';
import { GoogleTagManager } from '@next/third-parties/google';

const myFont = Inter({ subsets: ['latin'], display: 'swap' });

function ThemeVariables({ themeColors }) {
  if (!themeColors) return null;

  const cssVars = {
    ...generateHSLShades(themeColors.primary.hex, 'clr-primary'),
    ...generateHSLShades(themeColors.accent.hex, 'clr-accent'),
    '--clr-secondary-300': themeColors.secondary.hex,
  };

  return (
    <style jsx global>{`
      :root {
        ${Object.entries(cssVars)
        .map(([key, val]) => `${key}: ${val};`)
        .join('\n')}
      }
    `}</style>
  );
}

function shouldLoadGTM() {
  if (typeof window === 'undefined') return false;
  const isProd = process.env.NODE_ENV === 'production';
  const isExcludedDomain = ['dev.plpcsanjose.com'].includes(window.location.hostname);
  return isProd && !isExcludedDomain;
}

export default function App({ Component, pageProps, router }) {
  const { siteSettings } = pageProps;

  return (
    <SiteSettingsContext.Provider value={siteSettings}>
      <StoreProvider>
        <UserProvider>
          <SpecialsProvider>
            <CartProvider>
              <Head>
                <meta
                  name="viewport"
                  content="width=device-width, initial-scale=1.0, maximum-scale=5.0"
                />
              </Head>
              {shouldLoadGTM() && <GoogleTagManager gtmId="GTM-KQQS78R" />}
              <DefaultSeo
                openGraph={{
                  type: 'website',
                  locale: 'en_US',
                  url: process.env.NEXT_PUBLIC_SITE_URL,
                  siteName: 'Purple Lotus',
                }}
                twitter={{
                  handle: '@purplelotusca',
                  site: '@purplelotusca',
                  cardType: 'summary_large_image',
                }}
              />

              <ThemeVariables themeColors={siteSettings?.themeColors} />

              <a href="#content" className="skip-to-main-content-link">
                Skip to main content
              </a>

              <div id="content" className={myFont.className}>
                <Layout router={router}>
                  <Component {...pageProps} />
                </Layout>
              </div>
            </CartProvider>
          </SpecialsProvider>
        </UserProvider>
      </StoreProvider>
    </SiteSettingsContext.Provider>
  );
}
